import { Button, message } from 'antd';
import { Formik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import { type PackageBlockSpecsFragment } from '../../../../api/fragments/PackageBlockSpecs';
import {
    BlockType,
    ContentRefinementSourceAction,
    ContentRefinementSourceKind,
    PackageKind,
} from '../../../../api/types';
import { GenericStateOnPageContextProvider } from '../../../../components/contexts/GenericStateOnPageContextManager';
import Form from '../../../../components/fields/Form';
import FormAutoTouch from '../../../../components/FormAutoTouch';
import hasPermissions from '../../../../utilities/hasPermissions';
import useDebounce from '../../../../utilities/useDebounce';
import useHandleError from '../../../../utilities/useHandleError';
import { type VariantConfiguratorDetailsPageProps } from '../utils';
import { useVariantConfigurator } from '../VariantConfiguratorContext';
import VariantConfiguratorLayout from '../VariantConfiguratorLayout';
import PackageBlockForm from './PackageBlockForm';
import useHandlePackageBlock from './useHandlePackageBlock';
import usePackageBlockValidator from './usePackageBlockValidator';
import { FORM_NAME, defaultPackageBlockValues, type PackageBlockFormValues } from './utils';

const PackageBlockTab = ({ activeTab, replaceTab, disabled = false }: VariantConfiguratorDetailsPageProps) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);
    const { variantConfigurator, refetch } = useVariantConfigurator();

    const validator = usePackageBlockValidator();

    const handlePackageBlock = useHandlePackageBlock();

    const packageBlock = useMemo(() => variantConfigurator.packageBlock, [variantConfigurator.packageBlock]);

    const getInitialValues = useCallback((block: PackageBlockSpecsFragment): PackageBlockFormValues => {
        if (!block) {
            return defaultPackageBlockValues;
        }

        return {
            type: BlockType.Package,
            packageBlockInput: {
                id: block.id,
                isActive: block.isActive,
                packageSettings: block.packageSettings.map(setting => ({
                    id: setting.id,
                    features: setting.features,
                    packageName: setting.packageName,
                    packageType: {
                        type:
                            setting.packageType.__typename === 'PackageTypeWithDescription'
                                ? PackageKind.Description
                                : PackageKind.Price,
                        packageTypeWithPriceInput: {
                            price:
                                setting.packageType.__typename === 'PackageTypeWithPrice'
                                    ? setting.packageType.price
                                    : 0,
                            financing:
                                setting.packageType.__typename === 'PackageTypeWithPrice'
                                    ? setting.packageType.financing
                                    : false,
                        },
                        packageTypeWithDescriptionInput: {
                            description:
                                setting.packageType.__typename === 'PackageTypeWithDescription'
                                    ? setting.packageType.description
                                    : '',
                        },
                    },
                    additionalDetails: setting.additionalDetails.map(item => ({
                        id: item.id,
                        title: item.title,
                        description: item.description,
                        image: item.image,
                    })),
                    sectionImage: setting.sectionImage,
                })),
            },
        };
    }, []);

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: hasPermissions(variantConfigurator.permissions, [
                permissionKind.updateConfigurator,
            ]),
            source: {
                kind: ContentRefinementSourceKind.Configurator,
                action: ContentRefinementSourceAction.Update,
                identifier: variantConfigurator.modelConfigurator.id,
            },
        }),
        [variantConfigurator]
    );

    const onSubmit = useDebounce(
        useHandleError<PackageBlockFormValues>(
            async (values, helpers) => {
                message.loading({
                    content: t('variantConfiguratorDetails:messages.packages.saveSubmitting'),
                    key: 'primary',
                    duration: 0,
                });

                await handlePackageBlock(values, variantConfigurator.id);

                message.success({
                    content: t('variantConfiguratorDetails:messages.packages.saveSuccessful'),
                    key: 'primary',
                });

                const updatedVariantConfigurator = await refetch();

                helpers.resetForm({
                    values: getInitialValues(updatedVariantConfigurator?.packageBlock),
                });
            },
            [getInitialValues, handlePackageBlock, refetch, t, variantConfigurator.id]
        )
    );

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <VariantConfiguratorLayout
                activeTab={activeTab}
                footer={[
                    !disabled && (
                        <Button key="submit" form={FORM_NAME} htmlType="submit" type="primary">
                            {t('variantConfiguratorDetails:actions.packages.save')}
                        </Button>
                    ),
                ]}
                replaceTab={replaceTab}
            >
                <Formik<PackageBlockFormValues>
                    initialValues={getInitialValues(packageBlock)}
                    onSubmit={onSubmit}
                    validate={validator}
                >
                    {({ handleSubmit }) => (
                        <Form id={FORM_NAME} name={FORM_NAME} onSubmitCapture={handleSubmit}>
                            <FormAutoTouch />
                            <PackageBlockForm disabled={disabled} />
                        </Form>
                    )}
                </Formik>
            </VariantConfiguratorLayout>
        </GenericStateOnPageContextProvider>
    );
};

export default PackageBlockTab;
